import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { actionProps, selectState } from '../../../utils/reduxActions';
import { Dimmer, Loader } from 'semantic-ui-react';
import { useEffect, useState } from 'react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TextField } from '@mui/material';
import { paymentActions } from './reducer';
import { invoiceActions } from '../Facturama/reducer';

interface PaymentOwnProps {
    paymentControls?: any;
    actions?: any;
    effectiveDate?: string;
    reference?: string;
    selectedInvoices?: any[];
    paymentForms?: Array<{ Name: string; Value: string }>;
}

type PaymentProps = PaymentOwnProps & RouteComponentProps;

const Payment: React.FC<PaymentProps> = props => {
    const { selectedInvoices, actions, paymentControls, paymentForms } = props;
    const [effectiveDate, setEffectiveDate] = useState<Date | null>(null);
    const [paymentConcept, setPaymentConcept] = useState<string>('');
    const [paymentForm, setPaymentForm] = useState<string>('');
    const [destinationAccountNum, setDestinationAccountNum] = useState<string>('');
    const [destinationRfc, setDestinationRfc] = useState<string>('');
    const [sourceAccountNum, setSourceAccountNum] = useState<string>('');
    const [sourceRfc, setSourceRfc] = useState<string>('');
    const [sourceForeignName, setSourceForeignName] = useState<string>('');

    const isTransferForm = paymentForm === '03';
    const totalAmount: number = selectedInvoices?.reduce((acc: number, invoice: any) => acc + (Number(invoice.Total) || 0), 0) || 0;
    const rfcRegex = /^[A-Z]{3,4}[0-9]{6}/;

    useEffect(() => {
        if (actions && actions.getPaymentForms) {
            actions.getPaymentForms();
        }
    }, [actions]);

    const submitPayment = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!effectiveDate) return;

        if (actions && actions.createPayment) {
            actions.createPayment({
                effectiveDate,
                paymentConcept,
                selectedInvoices,
                amount: totalAmount,
                paymentForm,
                destinationAccountNum: isTransferForm ? destinationAccountNum : undefined,
                destinationRfc: isTransferForm ? destinationRfc : undefined,
                sourceAccountNum: isTransferForm ? sourceAccountNum : undefined,
                sourceRfc: isTransferForm ? sourceRfc : undefined,
                sourceForeignName: isTransferForm ? sourceForeignName : undefined,
            });
        }
    }

    return (
        <form onSubmit={submitPayment}>
            <Dimmer
                active={paymentControls.loading}
                inverted
            >
                <Loader inverted>Generando complemento de pago.....</Loader>
            </Dimmer>
            
            <div className='m-auto text-center'>
                <h2 className='text-2xl font-bold my-4 '>
                    <span className='uppercase'>Generar complemento de pago</span>
                </h2>
                <hr className='mt-1 mb-4 md:min-w-full ' />
                <div className='grid'>
                    <span className='font-bold text-xl uppercase text-left mb-2'>Facturas seleccionadas:</span>
                </div>
                <div className="overflow-x-auto w-full">
                    <table className="min-w-full table-auto border-collapse">
                        <thead>
                            <tr className="bg-gray-100">
                            <th className="border px-4 py-2 text-center">Folio</th>
                                <th className="border px-4 py-2 text-center">No. Rastreo</th>
                                <th className="border px-4 py-2 text-center">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedInvoices?.map((invoice, index) => (
                                <tr key={index}>
                                    <td className="border px-4 py-2">{invoice.Folio}</td>
                                    <td className="border px-4 py-2">{invoice.trackingNumber || '-'}</td>
                                    <td className="border px-4 py-2">{invoice.Total}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 mb-4">
                    <div className="flex flex-col items-start">
                        <label className="mb-2 font-semibold text-left">
                            Fecha de pago <span className="text-red-500">*</span>
                        </label>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                className="w-full"
                                value={effectiveDate}
                                onChange={(newValue) => setEffectiveDate(newValue)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        variant: "outlined",
                                        placeholder: "Seleccione una fecha",
                                        size: "small"
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </div>
                    <div className="flex flex-col items-start">
                        <label className="mb-2 font-semibold text-left">
                            Total de facturas
                        </label>
                        <TextField
                            fullWidth
                            size="small"
                            value={totalAmount.toFixed(2)}
                            InputProps={{ readOnly: true }}
                            inputProps={{ className: 'cursor-default' }}
                            style={{ backgroundColor: '#f0f0f0' }}
                        />
                    </div>
                    <div className="flex flex-col items-start">
                        <label className="mb-2 font-semibold text-left">
                            Forma de pago <span className="text-red-500">*</span>
                        </label>
                        <select
                            className="w-full border-2 py-2 px-4 mt-0 text-base rounded-lg focus:outline-none"
                            value={paymentForm}
                            onChange={(e) => setPaymentForm(e.target.value)}
                        >
                            <option key={''} value={''} disabled></option>
                            {paymentForms?.map((form) => (
                                <option key={form.Value} value={form.Value}>
                                    {form.Value} - {form.Name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex flex-col items-start">
                        <label htmlFor="paymentConcept" className="mb-2 font-semibold text-left">
                            Concepto de pago (opcional)
                        </label>
                        <TextField
                            id="paymentConcept"
                            placeholder="Ingrese un concepto para el pago"
                            fullWidth
                            size="small"
                            value={paymentConcept}
                            onChange={(e) => setPaymentConcept(e.target.value)}
                        />
                    </div>
                </div>

                {isTransferForm && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2 mb-4">
                        <div className="flex flex-col items-start">
                            <label className="mb-2 font-semibold text-left">CLABE de destino <span className="text-red-500">*</span></label>
                            <TextField
                                placeholder="CLABE de 18 dígitos"
                                fullWidth
                                size="small"
                                value={destinationAccountNum}
                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]{18}', maxLength: 18 }}
                                onChange={(e) => setDestinationAccountNum(e.target.value.replace(/\D/g, '').slice(0, 18))}
                                error={destinationAccountNum.length > 0 && destinationAccountNum.length !== 18}
                                helperText={destinationAccountNum.length > 0 && destinationAccountNum.length !== 18 ? 'Debe tener 18 dígitos' : ''}
                            />
                        </div>
                        <div className="flex flex-col items-start">
                            <label className="mb-2 font-semibold text-left">RFC del banco receptor <span className="text-red-500">*</span></label>
                            <TextField
                                placeholder="RFC del banco receptor"
                                fullWidth
                                size="small"
                                value={destinationRfc}
                                onChange={(e) => setDestinationRfc(e.target.value.toUpperCase())}
                                error={destinationRfc.length > 0 && !rfcRegex.test(destinationRfc)}
                                helperText={destinationRfc.length > 0 && !rfcRegex.test(destinationRfc) ? 'Debe iniciar con 3 o 4 letras y 6 números' : ''}
                            />
                        </div>
                        <div className="flex flex-col items-start">
                            <label className="mb-2 font-semibold text-left">CLABE de origen <span className="text-red-500">*</span></label>
                            <TextField
                                placeholder="CLABE de 18 dígitos"
                                fullWidth
                                size="small"
                                value={sourceAccountNum}
                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]{18}', maxLength: 18 }}
                                onChange={(e) => setSourceAccountNum(e.target.value.replace(/\D/g, '').slice(0, 18))}
                                error={sourceAccountNum.length > 0 && sourceAccountNum.length !== 18}
                                helperText={sourceAccountNum.length > 0 && sourceAccountNum.length !== 18 ? 'Debe tener 18 dígitos' : ''}
                            />
                        </div>
                        <div className="flex flex-col items-start">
                            <label className="mb-2 font-semibold text-left">RFC del banco de origen <span className="text-red-500">*</span></label>
                            <TextField
                                placeholder="RFC del banco de origen"
                                fullWidth
                                size="small"
                                value={sourceRfc}
                                onChange={(e) => setSourceRfc(e.target.value.toUpperCase())}
                                error={sourceRfc.length > 0 && !rfcRegex.test(sourceRfc)}
                                helperText={sourceRfc.length > 0 && !rfcRegex.test(sourceRfc) ? 'Debe iniciar con 3 o 4 letras y 6 números' : ''}
                            />
                        </div>
                        <div className="flex flex-col items-start md:col-span-2">
                            <label className="mb-2 font-semibold text-left">Nombre del banco que generó el pago <span className="text-red-500">*</span></label>
                            <TextField
                                placeholder="Nombre del banco"
                                fullWidth
                                size="small"
                                value={sourceForeignName}
                                onChange={(e) => setSourceForeignName(e.target.value)}
                            />
                        </div>
                    </div>
                )}

                <div className='grid w-full grid-cols-1 m-auto my-6'>
                    <input
                        type='submit'
                        value='Generar complemento'
                        className={`bg-primary-inabit-red py-4 text-2xl text-white cursor-pointer rounded-xl ${
                        !effectiveDate || !paymentForm || (isTransferForm && (!destinationAccountNum || destinationAccountNum.length !== 18 || !destinationRfc || !sourceAccountNum || sourceAccountNum.length !== 18 || !sourceRfc || !sourceForeignName))
                            ? 'opacity-50 cursor-not-allowed'
                            : 'hover:bg-primary-inabit-coffe'
                        }`}
                        disabled={!effectiveDate || !paymentForm || (isTransferForm && (!destinationAccountNum || destinationAccountNum.length !== 18 || !destinationRfc || !sourceAccountNum || sourceAccountNum.length !== 18 || !sourceRfc || !sourceForeignName))}
                    ></input>
                </div>
            </div>
        </form>
    )
}

const withConnect = connect(
    selectState(
      'payment.paymentControls',
      'app.profile',
      'invoice.paymentForms',
    ),
    actionProps({ ...paymentActions, ...invoiceActions }),
  );

  export default withRouter(withConnect(Payment));
  